import { initializeIntercomWithUser } from "../helpers/intercomHelper";

/**
 * Production-ready Intercom Service
 * Automatically manages Intercom lifecycle based on app authentication state
 */
class IntercomService {
  constructor() {
    this.initialized = false;
    this.currentUser = null;
  }

  /**
   * Initialize Intercom when user logs in
   * @param {Object} loginState - Redux login state
   * @param {Object} assistantDetails - Redux assistant details
   */
  async initializeForUser(loginState) {
    try {
      if (!loginState.isAuthenticated) {
        console.log(
          "[Intercom] User not authenticated, skipping initialization"
        );
        return;
      }

      const userData = this.prepareUserData(loginState.updatedAgent);

      // Check if we have the minimum required data for Intercom
      if (!userData.user_id && !userData.email) {
        console.error(
          "[Intercom] Missing required user identification (user_id or email)"
        );
        return;
      }

      const userId = userData.user_id || userData.email;

      // Check if we're already initialized for this user
      if (this.currentUser && this.currentUser.user_id === userId) {
        console.log("[Intercom] Already initialized for user:", userId);
        return;
      }

      console.log("[Intercom] Initializing with user data:", userData);

      // Initialize Intercom with user data
      await initializeIntercomWithUser(userData, {});

      this.initialized = true;
      this.currentUser = { ...loginState.user, user_id: userId };

      console.log("[Intercom] Successfully initialized for user:", userId);
    } catch (error) {
      console.error("[Intercom] Error initializing for user:", error);
    }
  }

  /**
   * Prepare user data for Intercom from login state
   * @param {Object} user - User object from login state
   * @returns {Object} Formatted user data
   */
  prepareUserData(user) {
    if (!user || Object.keys(user).length === 0) return {};

    // Handle both nested and flat user structures
    let actualUser = user;

    // Check if user is nested (has a key with user data)
    if (typeof user === "object" && Object.keys(user).length === 1) {
      const firstKey = Object.keys(user)[0];
      actualUser = user[firstKey];
    }

    const userData = {};

    // Use _id as user_id if available, otherwise use id
    if (actualUser?._id) {
      userData.user_id = String(actualUser._id);
    } else if (actualUser?.id) {
      userData.user_id = String(actualUser.id);
    }

    // Email is required for Intercom identification
    if (actualUser?.email) {
      userData.email = actualUser.email;
    }

    // Name field
    if (actualUser?.name) {
      userData.name = actualUser.name;
    } else if (actualUser?.displayName) {
      userData.name = actualUser.displayName;
    }

    // Phone field
    if (actualUser?.phone) {
      userData.phone = actualUser.phone;
    } else if (actualUser?.contact) {
      userData.phone = actualUser.contact;
    }

    // Created at timestamp
    if (actualUser?.createdAt) {
      userData.created_at = Math.floor(
        new Date(actualUser.createdAt).getTime() / 1000
      );
    } else if (actualUser?.created_at) {
      userData.created_at = actualUser.created_at;
    }

    console.log("Prepared user data for Intercom:", userData);
    return userData;
  }

  /**
   * Update user attributes in Intercom
   * @param {Object} userData - User data to update in Intercom
   * @returns {boolean} True if update was successful
   */
  updateUser(userData) {
    if (!this.isInitialized()) return false;
    try {
      window.Intercom("update", userData);
      return true;
    } catch {
      return false;
    }
  }

  updateUserAttributes(attributes = {}) {
    if (!this.isInitialized()) {
      console.error("[Intercom] Service not initialized properly");
      console.log("Debug info:", {
        hasWindow: typeof window !== "undefined",
        hasIntercom: !!(window && window.Intercom),
        isLoaded: this.isLoaded,
        initialized: this.initialized,
      });
      return false;
    }

    try {
      // Try to update using the Intercom API
      window.Intercom("update", attributes);

      return true;
    } catch (error) {
      console.error(":x: [Intercom] Failed to update user attributes:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        attributes: attributes,
      });
      return false;
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the custom event
   * @param {Object} eventData - Event data
   */
  trackCustomEvent(eventName, eventData = {}) {
    // Ensure event name follows convention
    const formattedEventName = eventName.toLowerCase().replace(/\s+/g, "_");

    const metadata = {
      event_type: "custom",
      timestamp: Date.now(),
      ...eventData,
    };

    this.trackEvent(formattedEventName, metadata);
  }

  /**
   * Ensure Intercom is initialized for the correct user context
   * @param {Object} user - User object containing id and assistantId
   * @returns {Promise<void>}
   */
  async ensureCorrectUserContext(user) {
    if (!user || !user.id || !user.assistantId) return;
    const expectedUserId = `${user.id}_${user.assistantId}`;
    if (!this.currentUser || this.currentUser.user_id !== expectedUserId) {
      // Prepare a minimal user object for initialization
      const userData = this.prepareUserData({
        ...user,
        assistantId: user.assistantId,
      });
      await initializeIntercomWithUser(userData, {});
      this.currentUser = { ...user, user_id: expectedUserId };
      this.initialized = true;
    }
  }

  /**
   * Track event and update user attributes, always using the correct user context
   * @param {string} eventName
   * @param {Object} eventMetadata
   * @param {Object|null} userAttributes
   * @param {Object|null} user - User object (must have id and assistantId)
   */
  async trackEventWithAttributes(
    eventName,
    eventMetadata,
    userAttributes = null,
    user = null
  ) {
    try {
      if (user && user.id && user.assistantId) {
        await this.ensureCorrectUserContext(user);
      }
      if (!this.isInitialized()) {
        console.warn("[Intercom] Not initialized, cannot track event");
        return false;
      }
      // Always include user_id and assistantId in event metadata
      const user_id =
        user?.id && user?.assistantId
          ? `${user.id}_${user.assistantId}`
          : this.currentUser?.user_id;
      const assistantId = user?.assistantId || this.currentUser?.assistantId;
      const enrichedMetadata = {
        ...eventMetadata,
        user_id,
        assistant_id: assistantId,
        timestamp: Date.now(),
        url:
          typeof window !== "undefined" ? window.location.pathname : undefined,
      };
      window.Intercom("trackEvent", eventName, enrichedMetadata);

      if (userAttributes && Object.keys(userAttributes).length > 0) {
        this.updateUserAttributes(userAttributes);
      }
      return true;
    } catch (error) {
      console.error(
        `:x: [Intercom] Failed to track event ${eventName}:`,
        error
      );
      return false;
    }
  }

  /**
   * Track a basic event, always using the correct user context
   * @param {string} eventName
   * @param {Object} metadata
   * @param {Object|null} user - User object (must have id and assistantId)
   */
  async trackEvent(eventName, metadata = {}, user = null) {
    try {
      if (user && user.id && user.assistantId) {
        await this.ensureCorrectUserContext(user);
      }
      if (!this.isInitialized()) return;
      const user_id =
        user?.id && user?.assistantId
          ? `${user.id}_${user.assistantId}`
          : this.currentUser?.user_id;
      const assistantId = user?.assistantId || this.currentUser?.assistantId;
      const enrichedMetadata = {
        ...metadata,
        user_id,
        assistant_id: assistantId,
        timestamp: Date.now(),
        source: "web",
      };
      console.log("Koca: enrichedMetadata ", enrichedMetadata);

      window.Intercom("trackEvent", eventName, enrichedMetadata);
    } catch (error) {
      console.error("Failed to track Intercom event:", error);
    }
  }

  /**
   * Check if Intercom is initialized
   * @returns {boolean} True if Intercom is initialized
   */
  isInitialized() {
    return (
      typeof window !== "undefined" && typeof window.Intercom === "function"
    );
  }

  /**
   * Debug method to check Intercom status and user data
   */
  debugIntercomStatus() {
    console.log("🔍 [Intercom Debug] Current status:");
    console.log("- Service initialized:", this.initialized);
    console.log("- Current user:", this.currentUser);
    console.log(
      "- Window Intercom available:",
      typeof window !== "undefined" && typeof window.Intercom === "function"
    );
    console.log("- Intercom settings:", window.intercomSettings || "Not set");

    if (typeof window !== "undefined" && window.Intercom) {
      try {
        console.log("- Intercom visitor ID:", window.Intercom("getVisitorId"));
      } catch (error) {
        console.log("- Could not get visitor ID:", error.message);
      }
    }
  }

  /**
   * Force re-initialization of Intercom with user data
   * @param {Object} userData - User data to initialize with
   */
  async forceReinitialize(userData) {
    console.log(
      "🔄 [Intercom] Force re-initializing with user data:",
      userData
    );

    try {
      // Reset current state
      this.initialized = false;
      this.currentUser = null;

      // Clear existing Intercom
      if (typeof window !== "undefined" && window.Intercom) {
        try {
          window.Intercom("shutdown");
        } catch (error) {
          console.warn("Could not shutdown Intercom:", error);
        }
      }

      // Re-initialize
      await this.initializeForUser({
        isAuthenticated: true,
        updatedAgent: userData,
      });

      console.log("✅ [Intercom] Force re-initialization completed");
    } catch (error) {
      console.error("❌ [Intercom] Force re-initialization failed:", error);
    }
  }

  /**
   * Manual initialization method for testing - can be called from browser console
   * @param {Object} userData - User data object
   */
  async manualInitialize(userData = {}) {
    console.log("🔧 [Intercom] Manual initialization with:", userData);

    try {
      // Use the provided user data or fallback to test data
      const testUserData =
        Object.keys(userData).length > 0
          ? userData
          : {
              user_id: "6842ef4d5edd9bde470c1714",
              email: "romit@aisensy.com",
              name: "owner",
              phone: "911234567890",
              created_at: 1716990532,
            };

      // await this.forceReinitialize(testUserData);

      // Show the messenger after initialization
      setTimeout(() => {
        this.showMessenger();
      }, 2000);

      return true;
    } catch (error) {
      console.error("❌ [Intercom] Manual initialization failed:", error);
      return false;
    }
  }

  /**
   * Show the Intercom messenger
   */
  showMessenger() {
    if (this.isInitialized()) {
      window.Intercom("show");
      console.log("📱 [Intercom] Messenger shown");
    } else {
      console.warn("⚠️ [Intercom] Cannot show messenger - not initialized");
    }
  }

  /**
   * Hide the Intercom messenger
   */
  hideMessenger() {
    if (this.isInitialized()) {
      window.Intercom("hide");
      console.log("📱 [Intercom] Messenger hidden");
    } else {
      console.warn("⚠️ [Intercom] Cannot hide messenger - not initialized");
    }
  }
}

const intercomService = new IntercomService();

// Make it available globally for debugging
if (typeof window !== "undefined") {
  window.intercomService = intercomService;
  console.log(
    "🔧 [Intercom] Service available globally as window.intercomService"
  );
  console.log("🔧 [Intercom] You can call:");
  console.log("   - window.intercomService.manualInitialize()");
  console.log("   - window.intercomService.debugIntercomStatus()");
  console.log("   - window.intercomService.showMessenger()");
}

export default intercomService;
